import i18n from '@/i18n';
import { PhEnvelopeSimple } from '@phosphor-icons/vue';
import { useFileManagerMailDraft } from '../../core/useFileManagerMailDraft';
import type { FileManagerTool } from '../fileManagerTool';

/**
 * "Skapa outlook mail": creates a blank draft in the user's own mailbox with the selected files attached
 * as byte copies.
 *
 * A factory, not a literal, because `run` closes over injected state — see FileManagerToolFactory.
 * useFileManagerTools calls this once, from within its own setup() call.
 *
 * Named `outlook-mail-copy` rather than `mail`, matching its permission: a future tool may send
 * SharePoint LINKS instead of copies, and `tool:outlook-mail-reference` has to stay free to mean that.
 * The two are materially different decisions — one exports bytes, the other grants access.
 */
export const createOutlookMailCopyTool = (): FileManagerTool => {
  const { t } = i18n.global;
  const { start } = useFileManagerMailDraft();

  return {
    id: 'outlook-mail-copy',
    permission: 'tool:outlook-mail-copy',
    label: t('fileManager.tools.outlookMail.outlookMailCopyTool.label'),
    icon: PhEnvelopeSimple,
    // Structural only, and that is the whole rule: files, at least one of them. Deliberately says
    // nothing about total size — a menu entry that silently vanishes because a selection grew past
    // 18 MB is a mystery to the user, so that check lives inside the run, in the modal, with a message
    // naming the actual number.
    //
    // `every` rather than `some`: a mixed selection gets no tool at all rather than quietly mailing the
    // files and dropping the folders.
    isAvailable: (target) =>
      target.length > 0 && target.every((item) => item.type === 'file'),
    // Synchronous, as the descriptor requires: this hands off to the composable, whose `start` is itself
    // synchronous up to the point it kicks off the Mail.ReadWrite acquisition — which is what keeps the
    // consent popup inside the click gesture (R5).
    run: (target) => start(target),
  };
};
